import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { surveyTemplatesService } from 'api/administration/surveyTemplates'
import { useNotification } from 'hooks/notifications/useNotification'
import type { SurveyTemplateRow, SurveyTemplateStatus } from '../common/types'
import { DEFAULT_PER_PAGE, TRANSLATION_PREFIX } from '../constants'

export interface SurveyTemplatesFilters {
  search: string
  status: SurveyTemplateStatus
  page: number
  perPage: number
}

const initialFilters = (
  status: SurveyTemplateStatus,
  perPage: number
): SurveyTemplatesFilters => ({
  search: '',
  status,
  page: 1,
  perPage,
})

interface Options {
  status?: SurveyTemplateStatus
  perPage?: number
}

/**
 * Search, status filter, pagination and rows of the survey bank. Changing the
 * search, the status or the page size restarts at page 1. Responses that
 * arrive after a newer request was issued are discarded.
 */
export const useSurveyTemplates = ({
  status = 'active',
  perPage = DEFAULT_PER_PAGE,
}: Options = {}) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const [filters, setFilters] = useState<SurveyTemplatesFilters>(() =>
    initialFilters(status, perPage)
  )
  const [rows, setRows] = useState<SurveyTemplateRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const latestRequest = useRef(0)

  // Read through a ref so the fetch effect depends only on the request.
  const notifyError = useRef(() => undefined as void)
  notifyError.current = () => {
    notification.error(t(`${TRANSLATION_PREFIX}.errors.loadList`))
  }

  const { search, page } = filters
  const currentStatus = filters.status
  const currentPerPage = filters.perPage

  useEffect(() => {
    latestRequest.current += 1
    const requestId = latestRequest.current
    const isCurrent = () => requestId === latestRequest.current

    setLoading(true)
    setFailed(false)
    surveyTemplatesService
      .list({
        search,
        status: currentStatus,
        page,
        perPage: currentPerPage,
      })
      .then(({ data }) => {
        if (!isCurrent()) return
        setRows(data.data.map(({ attributes }) => attributes))
        setTotal(data.meta?.total ?? data.data.length)
      })
      .catch(() => {
        if (!isCurrent()) return
        setRows([])
        setTotal(0)
        setFailed(true)
        notifyError.current()
      })
      .finally(() => {
        if (isCurrent()) setLoading(false)
      })
  }, [search, currentStatus, page, currentPerPage, reloadKey])

  const setSearch = useCallback((value: string) => {
    setFilters((current) => ({ ...current, search: value, page: 1 }))
  }, [])

  const setStatus = useCallback((value: SurveyTemplateStatus) => {
    setFilters((current) => ({ ...current, status: value, page: 1 }))
  }, [])

  const setPage = useCallback((value: number) => {
    setFilters((current) => ({ ...current, page: value }))
  }, [])

  const setPerPage = useCallback((value: number) => {
    setFilters((current) => ({ ...current, perPage: value, page: 1 }))
  }, [])

  const reload = useCallback(() => setReloadKey((key) => key + 1), [])

  return {
    rows,
    total,
    loading,
    failed,
    filters,
    setSearch,
    setStatus,
    setPage,
    setPerPage,
    reload,
  }
}
